import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateUserMutation } from 'app/services/dashboardApi';
import {
  useGetUserSettingsReportColorsQuery,
  type IUserSettingsReportColor,
} from 'app/services/userSettingsApi';

/**
 * The Colour tab — `color/view/color.js` (123 LOC) + `color/tmpl/color.tmpl`
 * (55 LOC), ported whole. It is the ONLY tab in wave U-a and it carries the
 * page's smallest real write:
 *
 *     PATCH /api/v1.0/users/<id>  {"report_color":"(178,34,34)"}     (§6 W10)
 *
 * ═══ THE THREE PIECES OF MUTABLE STATE, AND WHY THEY ARE IN A REF ═══
 *
 * Backbone views mutate `self.*` freely and repaint ONLY when `render()` is
 * called. This view calls `render()` exactly twice in its whole life:
 *   1. once when `colorCollection.fetch()` resolves (`color.js:33-36`), and
 *   2. once after the PATCH resolves (`color.js:87-89`).
 * Every other handler mutates `self.selected_color` / `self.selected_hex_color`
 * / `self.input_hex` and touches the DOM directly with jQuery — WITHOUT
 * repainting. That gap is not an implementation detail, it IS quirks Q7 and Q8:
 *
 *   Q7 — selecting a palette row moves the `.selected` border and enables Apply
 *        but does NOT update the big swatch / RGB / Hex panel, because
 *        `selectColor` (`:57-64`) never calls `render()`.
 *   Q8 — the `<input type="color">` NEVER follows the applied colour:
 *        `render()` guards `if (!self.input_hex) self.input_hex = hex`
 *        (`:44`), so `input_hex` is written once, at the first render, and
 *        `selectColor` never touches it. After applying Firebrick the panel
 *        reads `#b22222` while the picker still shows `#150b84`. Only
 *        `changeColor` (typing in the picker itself) updates it.
 *
 * A straightforward `useState` for `selected_color` would repaint the panel on
 * every palette click and destroy both quirks. So the mutable fields live in a
 * ref (`self`, below) and a SNAPSHOT of what the template was last handed lives
 * in React state (`snap`). `doRender()` is `render()`: it is the only thing
 * that copies ref → snapshot, and it is called from exactly the two places
 * legacy calls `render()`.
 *
 * ═══ WHAT IS NOT REPRODUCED (and why it is not a deviation) ═══
 *
 * `render()` also does `self.scrollTop && $('.reports-color-list').scrollTop(
 * self.scrollTop)` (`:53`), paired with `apply`'s `self.scrollTop = $('.reports
 * -color-list').scrollTop()` (`:78`). That pair exists solely because
 * `$el.html(...)` throws the scrolled list away and rebuilds it. React
 * re-renders the same DOM nodes, so the scroll position survives on its own and
 * a capture/restore round-trip would be a literal no-op. Observable behaviour
 * is identical: the list stays where it was after Apply.
 */

/** `color.js:95-98` — `"(21,11,132)"` → `["21","11","132"]`. */
const splitRgb = (rgb: string): string[] => rgb.slice(1, -1).split(',');

/**
 * `color.js:100-107`, verbatim including the clamp-then-shift arithmetic and
 * the string arguments `splitRgb` feeds it (`Math.min(255, "21")` coerces).
 * Lower-case output, e.g. `#150b84`.
 */
const rgbToHex = (r: unknown, g: unknown, b: unknown): string => {
  const cr = Math.max(0, Math.min(255, r as number));
  const cg = Math.max(0, Math.min(255, g as number));
  const cb = Math.max(0, Math.min(255, b as number));
  // eslint-disable-next-line no-bitwise
  return `#${((1 << 24) + (cr << 16) + (cg << 8) + cb).toString(16).slice(1)}`;
};

/** `color.js:109-117`, verbatim. */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const bigint = parseInt(hex.replace(/^#/, ''), 16);
  /* eslint-disable no-bitwise */
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  /* eslint-enable no-bitwise */
};

/** What the Handlebars template was last handed — i.e. the last `render()`. */
interface RenderSnapshot {
  /** `{{selected_color}}` — the RGB string, parentheses included. */
  selected_color: string;
  /** `{{hex}}` — `selected_hex_color || rgbToHex(...)`. */
  hex: string;
  /** `{{input_hex}}` — the `<input type="color">`'s `value`. Written once (Q8). */
  input_hex: string;
  /**
   * Bumped on every `render()`. Used as the picker input's `key` so that a
   * render REPLACES the uncontrolled input with a fresh one carrying the
   * current `input_hex` as its default — the React equivalent of `$el.html()`
   * rebuilding that `<input value="{{input_hex}}">`.
   */
  gen: number;
}

interface ColorTabProps {
  /** `fn['current.user']`'s id — the PATCH target. */
  userId: number;
  /**
   * `user.get('report_color')`, read ONCE at construction (`color.js:31`).
   * Later changes to the cached user are deliberately ignored: legacy holds the
   * same Backbone model instance and only ever writes to it.
   */
  reportColor: string;
}

const ColorTab = ({ userId, reportColor }: ColorTabProps) => {
  // `color.js:32-36` — `new ColorCollection().fetch()`. Fires on EVERY boot
  // (this component is mounted whatever the active tab is), which is why the
  // request appears at t=52ms in the discovery's `#submittal` boot trace.
  const { data: colors } = useGetUserSettingsReportColorsQuery();

  // §6 W10. Reused from `dashboardApi` rather than redeclared — same URL, same
  // verb, and it keeps the header's cached user in one place.
  const [updateUser] = useUpdateUserMutation();

  /** The Backbone view's mutable fields (`self.*`). NEVER read during render. */
  const self = useRef({
    selected_color: reportColor,
    /** `''` after a palette click (Q9 — `data-hex` is always empty). */
    selected_hex_color: undefined as string | undefined,
    input_hex: undefined as string | undefined,
  });

  const [snap, setSnap] = useState<RenderSnapshot | null>(null);
  /**
   * Which row wears `.selected`. Legacy sets this with jQuery in `selectColor`
   * and recomputes it from `selected_color` on every `render()` (the template's
   * `{{#ifCompare value '==' ../selected_color}}`). Kept separate from
   * `snap.selected_color` precisely so a palette click can move the border
   * without repainting the panel (Q7) — and so that applying a PICKER colour,
   * which never touched this, clears the selection on the next render.
   */
  const [rowSelected, setRowSelected] = useState<string | null>(null);
  /**
   * `.apply-btn`'s `non-clickable`. The template hard-codes the class
   * (`color.tmpl:51`), so every `render()` disables the button again — which is
   * why Apply goes back to disabled after a successful save (§6 W10).
   * `common.css:28337` makes `.non-clickable` `pointer-events:none`, so the
   * disabled button genuinely cannot be clicked; no JS guard is added here
   * because legacy has none.
   */
  const [applyEnabled, setApplyEnabled] = useState(false);

  const pickRef = useRef<HTMLInputElement | null>(null);

  /** `color.js:39-55` — `render()`. The ONLY ref → snapshot copy. */
  const doRender = useCallback(() => {
    const s = self.current;
    const [r, g, b] = splitRgb(s.selected_color);
    const hex = s.selected_hex_color || rgbToHex(r, g, b);
    if (!s.input_hex) s.input_hex = hex; // ★ Q8: written once, never again
    setSnap((prev) => ({
      selected_color: s.selected_color,
      hex,
      input_hex: s.input_hex as string,
      gen: (prev?.gen ?? 0) + 1,
    }));
    setRowSelected(s.selected_color);
    setApplyEnabled(false);
  }, []);

  // `color.js:33-36` — the first and only automatic render, on fetch success.
  // Guarded so an RTK Query refetch (which legacy has no equivalent of) cannot
  // repaint the panel and undo an in-flight Q7/Q8 selection.
  const didFirstRender = useRef(false);
  useEffect(() => {
    if (!colors || didFirstRender.current) return;
    didFirstRender.current = true;
    doRender();
  }, [colors, doRender]);

  /**
   * `color.js:57-64` selectColor. Moves the border, enables Apply, records the
   * pending value — and does NOT re-render, which is Q7.
   *
   * ★ Q9 lives here: legacy reads `$(e.currentTarget).data('hex')`, and the
   * template emits `data-hex="{{hexCode}}"` in camelCase while the API field is
   * `hexcode`. The attribute is therefore `""` on all 139 rows and this assigns
   * the empty string — which is falsy, so `render()`'s
   * `selected_hex_color || rgbToHex(...)` always falls through to the computed
   * hex. Assigning `''` (not the row's real hexcode) is the parity-correct move.
   */
  const selectColor = (row: IUserSettingsReportColor) => {
    setRowSelected(row.value);
    setApplyEnabled(true);
    self.current.selected_color = row.value;
    self.current.selected_hex_color = '';
  };

  /**
   * `color.js:66-73` changeColor, bound to `input` on the native picker. Updates
   * all three mutable fields and enables Apply — but does not re-render, so the
   * panel keeps showing the old colour and the palette keeps its old
   * `.selected` row until Apply repaints.
   */
  const changeColor = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    self.current.selected_hex_color = value;
    const converted = hexToRgb(value);
    self.current.selected_color = `(${converted.r},${converted.g},${converted.b})`;
    self.current.input_hex = value;
    setApplyEnabled(true);
  };

  /**
   * `color.js:75-90` apply — the write. `user.save({report_color}, {patch:true})`
   * then `render()` from the IN-MEMORY model; legacy re-fetches neither
   * `/users/<id>` nor the palette. A failed save leaves everything untouched
   * (Backbone's `.then` success branch simply never runs).
   */
  const apply = async () => {
    try {
      await updateUser({ id: userId, body: { report_color: self.current.selected_color } }).unwrap();
      doRender();
    } catch {
      /* legacy has no error branch here */
    }
  };

  /** `color.js:92-94` clickToOpen — `$('#col-pick').click()`. */
  const clickToOpen = () => pickRef.current?.click();

  /**
   * `color.tmpl:48` carries `autofocus="true"` — and on legacy that attribute is
   * NOT inert, which an earlier revision of this comment got wrong.
   *
   * ★ MEASURED on the legacy page (dev3 `urls_legacy`, U-f verify), because the
   * previous note asserted the opposite from reading alone:
   *   · land on `/user_settings/#color`  → `document.activeElement` IS `#col-pick`
   *   · land on `#submittal` / `#proposal` / NO hash → activeElement is `<body>`
   *   · land elsewhere, THEN click the Color tab → still NOT focused (the tab
   *     anchor keeps focus); clicking away and back does not focus it either.
   *
   * That is exactly Chrome's autofocus algorithm, which runs ONCE when the
   * element is inserted and only focuses it if it is actually rendered. The
   * colour view's `$el.html()` happens inside `colorCollection.fetch().then(...)`,
   * so the input is inserted late: on a `#color` landing `#color_format` is
   * already un-hidden and the focus takes; on any other landing the pane still
   * carries `.hidden` (`display:none`), the element is unfocusable, and the
   * algorithm silently does nothing. A later tab click only removes `.hidden` —
   * autofocus has already been spent and never re-runs.
   *
   * Reproduced here mechanically rather than by branching on the boot tab, so
   * the rule stays the browser's: attach the attribute, then — exactly once per
   * element, matching "once at insertion" — focus it IF IT IS RENDERED.
   *
   * ★ WHY THE ONE-SHOT `useRef` GUARD IS LOAD-BEARING: `attachPicker` is
   * re-created on every render, so React detaches and re-attaches this ref on
   * every render. Without the guard the picker would re-focus itself on every
   * re-render of the tab (and steal focus mid-interaction), which is neither
   * legacy's behaviour nor anything a user would want.
   *
   * The attribute matters independently of focus: `main.js:46`'s Enter branch
   * reads `.popup-wrapper.active button[autofocus="true"]`, and this input being
   * the page's ONLY `autofocus` holder — and not a `button`, and not inside a
   * popup — is precisely why Enter is inert everywhere (§8 Q17).
   */
  const autofocusSpent = useRef(false);
  const attachPicker = (el: HTMLInputElement | null) => {
    pickRef.current = el;
    if (!el) return;
    el.setAttribute('autofocus', 'true');
    if (autofocusSpent.current) return;
    autofocusSpent.current = true;
    // "Is it rendered?" — the same `offsetWidth || offsetHeight` test the verify
    // harness used to measure the legacy element (true on a `#color` landing,
    // false when `#color_format` still has `.hidden`). Deliberately NOT
    // `offsetParent`, which also goes null under a `position:fixed` ancestor.
    if (el.offsetWidth || el.offsetHeight) el.focus();
  };

  // `#color_format` stays EMPTY until the fetch resolves — the view's `render()`
  // lives inside `colorCollection.fetch().then(...)`.
  if (!colors || !snap) return null;

  return (
    <div className="row full-height d-flex">
      <div className="col-xs-30 full-height">
        {/* Static decoration: one item, no handler (§5.4). */}
        <ul className="colors-menu">
          <li className="colors-menu-item selected">Reports</li>
        </ul>
        <div className="selected-color-wrapper">
          <div className="selected-color-code">
            <div className="selected-text">Selected Report Color</div>
            <div className="selected-color-rgb">RGB</div>
            <div className="selected-color-rgb">Hex Code</div>
          </div>
          <div className="selected-color-code">
            {/* `style="background: rgb{{selected_color}};"` — the RGB string
                already carries its own parentheses. */}
            <div
              className="color-rectangle mobile-width-120"
              style={{ background: `rgb${snap.selected_color}` }}
            />
            <div className="selected-color-rgb">{snap.selected_color}</div>
            <div className="selected-color-rgb">{snap.hex}</div>
          </div>
        </div>
      </div>
      <div className="col-xs-70 full-height">
        <div className="right-column row mr-0">
          <div className="col-xs-12 full-height col-md-7 right-column-scrollable pt-0 pr-0">
            <div className="available_fields block-title">Available Colors</div>
            <div className="each-color-row each-color-row-title">
              <div className="p-0 title-col color-column">COLOR</div>
              <div className="title-col name-column">NAME</div>
              <div className="centered-column title-col rgb-column">RGB</div>
              <div className="centered-column title-col hex-column">HEX CODE</div>
            </div>
            <div className="reports-color-list">
              {colors.map((row) => (
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
                <div
                  key={row.id}
                  className={`each-color-row each-color-row-item${
                    rowSelected === row.value ? ' selected' : ''
                  }`}
                  data-value={row.value}
                  /* ★ Q9 — `{{hexCode}}` does not exist on the payload, so this
                     attribute is empty on every row in legacy. Kept, empty. */
                  data-hex=""
                  onClick={() => selectColor(row)}
                >
                  <div
                    className="color-req p-0 color-column mobile-width-120"
                    style={{ background: `rgb${row.value}` }}
                  />
                  <div className="centered-column name-column">{row.name}</div>
                  <div className="centered-column rgb-column">{row.value}</div>
                  <div className="centered-column hex-column">{row.hexcode}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-xs-12 col-md-5 left-column-scrollable pt-0 pl-0">
            <div className="available_fields block-title">Color Picker</div>
            <div className="color-picker-wrapper">
              {/* Uncontrolled + re-keyed per render: React must not fight the
                  live value while the OS picker is open, and a `render()` must
                  replace it with `input_hex` — which after an Apply is still
                  the ORIGINAL hex (Q8). */}
              <input
                key={snap.gen}
                type="color"
                id="col-pick"
                name="head"
                defaultValue={snap.input_hex}
                className="color-picker-wrapper-input mobile-width-120"
                ref={attachPicker}
                onInput={changeColor}
              />
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div className="note col-pick-note" onClick={clickToOpen}>
                Click to open
              </div>
            </div>
            <button className={`btn apply-btn${applyEnabled ? '' : ' non-clickable'}`} onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorTab;
